/**
 * SessionScheduler — premium dark/electric-blue booking screen.
 *
 * Design spec: matches the user-supplied mock (dark navy → black gradient
 * background, electric-blue accents `#00B4FF`, glassmorphism cards). Reuses
 * the shared <AppLayout> top/bottom nav already mounted in App.jsx.
 *
 * Behaviour:
 *   • Reads `/api/sessions/quota` for the current user's weekly/monthly
 *     allotment and renders the "Your Weekly Session" progress bar.
 *   • Horizontal 7-day date picker (today + next 6 days, CST).
 *   • Pulls `/api/sessions/available-slots?date=…` for the trainer assigned
 *     to the current user; greys out time slots that overlap an existing
 *     booking on that trainer's calendar.
 *   • Books with `base44.entities.TrainingSession.create(...)`.
 *   • Lists the user's upcoming sessions and surfaces a JOIN VIDEO button
 *     in the join window (-5m → +60m of start_at); otherwise shows
 *     Reschedule + Cancel actions.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Video,
  Calendar as CalendarIcon,
  CalendarPlus,
  Clock,
  CalendarCheck,
  CalendarClock,
  ShieldCheck,
  Trash2,
  ChevronDown,
} from "lucide-react";

// CST 8-slot template (matches backend `/sessions/available-slots`).
const SLOT_TEMPLATE = [
  { time: "08:00", label: "8:00 AM" },
  { time: "09:30", label: "9:30 AM" },
  { time: "11:00", label: "11:00 AM" },
  { time: "13:00", label: "1:00 PM" },
  { time: "15:00", label: "3:00 PM" },
  { time: "16:30", label: "4:30 PM" },
  { time: "18:00", label: "6:00 PM" },
  { time: "19:30", label: "7:30 PM" },
];

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Must match the backend (`server.py::CANCEL_REFUND_HOURS`). Inside this
// window a cancellation forfeits the user's weekly session credit and
// the Reschedule button is disabled.
const CANCEL_REFUND_HOURS = 24;

function localDateKey(d) {
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");
}

function fmtCountdown(seconds) {
  if (seconds == null || seconds < 0) return "—";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

export default function SessionScheduler() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // --- Quota / progress bar ------------------------------------------------
  const { data: quota } = useQuery({
    queryKey: ["session-quota", user?.id],
    queryFn: async () => {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sessions/quota`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("ta_access_token") || ""}` },
      });
      return res.ok ? res.json() : null;
    },
    enabled: !!user?.id,
  });

  // --- Date carousel (today + up to 13 more = two weeks of options) ------
  const TOTAL_DAYS = 14;
  const PAGE_SIZE = 7;
  const [daysShown, setDaysShown] = useState(PAGE_SIZE);
  const dates = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 0; i < TOTAL_DAYS; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      out.push(d);
    }
    return out;
  }, []);
  const visibleDates = useMemo(() => dates.slice(0, daysShown), [dates, daysShown]);
  const [selectedDate, setSelectedDate] = useState(dates[0]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // --- Trainer conflict map for the selected date --------------------------
  const dateKey = localDateKey(selectedDate);
  const { data: slotData } = useQuery({
    queryKey: ["available-slots", dateKey, user?.assigned_trainer_id],
    queryFn: async () => {
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/sessions/available-slots`);
      url.searchParams.set("date", dateKey);
      if (user?.assigned_trainer_id) url.searchParams.set("trainer_id", user.assigned_trainer_id);
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("ta_access_token") || ""}` },
      });
      return res.ok ? res.json() : { slots: [] };
    },
    enabled: !!user?.id,
  });
  // Reset slot when date changes so a stale conflicting selection doesn't book.
  useEffect(() => { setSelectedSlot(null); }, [dateKey]);

  const bookedSet = useMemo(() => {
    const s = new Set();
    for (const slot of slotData?.slots || []) {
      if (slot.booked) s.add(slot.time);
    }
    return s;
  }, [slotData]);
  const slotIsoMap = useMemo(() => {
    const m = new Map();
    for (const slot of slotData?.slots || []) m.set(slot.time, slot.iso_utc);
    return m;
  }, [slotData]);
  // Slots that have already passed for the day (only relevant when the
  // selected date IS today). `nowTick` is refreshed every minute so the
  // 8:00 AM slot greys out at 8:01 AM automatically. We mark a slot as
  // passed once the local clock crosses its start time.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const pastSet = useMemo(() => {
    const s = new Set();
    const now = nowTick;
    for (const slot of slotData?.slots || []) {
      if (slot.iso_utc && new Date(slot.iso_utc).getTime() <= now) s.add(slot.time);
    }
    return s;
  }, [slotData, nowTick]);

  // If the user picked a slot and then it crossed into the past (e.g. they
  // opened the page at 7:58, selected 8:00, then it ticked to 8:01),
  // clear the selection so they can't book a past slot.
  useEffect(() => {
    if (!selectedSlot) return;
    const iso = slotIsoMap.get(selectedSlot);
    if (iso && new Date(iso).getTime() <= Date.now()) setSelectedSlot(null);
  }, [selectedSlot, slotIsoMap, nowTick]);

  // --- Upcoming sessions ---------------------------------------------------
  const { data: mySessions = [] } = useQuery({
    queryKey: ["my-sessions", user?.id],
    queryFn: () =>
      base44.entities.TrainingSession.filter({ sessionClientId: user.id }, "-date"),
    enabled: !!user?.id,
  });
  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return (mySessions || [])
      .filter((s) =>
        !["cancelled_refunded", "cancelled_late", "completed", "denied"].includes(s.status) &&
        new Date(s.date).getTime() > now - 60 * 60 * 1000, // include calls still mid-stream
      )
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [mySessions]);
  const nextUpcoming = upcomingSessions[0] || null;

  // --- Book ----------------------------------------------------------------
  // Trainer assignment: prefer the user's `assigned_trainer_id`. If they're
  // an admin or trainer testing the flow themselves and that field is null,
  // fall back to their own `_id` so the session is visible to them in the
  // external trainer dashboard. Regular clients with no trainer assigned
  // still book with `trainer_id: null` — surfaced to the user via the
  // disabled-state copy so support can hook them up.
  const effectiveTrainerId =
    user?.assigned_trainer_id ||
    ((user?.role === "admin" || user?.role === "trainer") ? user?.id : null);

  const bookMut = useMutation({
    mutationFn: async () => {
      const iso = slotIsoMap.get(selectedSlot);
      if (!iso) throw new Error("Pick a time slot first");
      return base44.entities.TrainingSession.create({
        date: iso,
        type: "personal_training",
        duration_min: 60,
        status: "pending",
        sessionClientId: user.id,
        trainer_id: effectiveTrainerId,
        notes: "",
      });
    },
    onSuccess: () => {
      toast({ title: "Session booked", description: "Your trainer will confirm shortly." });
      setSelectedSlot(null);
      qc.invalidateQueries({ queryKey: ["my-sessions", user?.id] });
      qc.invalidateQueries({ queryKey: ["session-quota", user?.id] });
      qc.invalidateQueries({ queryKey: ["available-slots"] });
    },
    onError: (err) => {
      toast({
        title: "Booking failed",
        description: err?.message || "Try again in a moment.",
        variant: "destructive",
      });
    },
  });

  // Cancellation flow. We keep the session being-cancelled in local state
  // so the confirmation dialog can render the late/refund warning copy
  // accurately. Late = inside the 24h refund window — user forfeits the
  // weekly credit.
  const [cancelTarget, setCancelTarget] = useState(null);
  const cancelHoursUntil = cancelTarget
    ? (new Date(cancelTarget.date).getTime() - Date.now()) / 36e5
    : null;
  const cancelIsLate = cancelHoursUntil != null && cancelHoursUntil < CANCEL_REFUND_HOURS;

  const cancelMut = useMutation({
    mutationFn: async ({ sessId, forceLate }) => {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sessions/${sessId}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("ta_access_token") || ""}`,
          },
          body: JSON.stringify({ force_late: !!forceLate }),
        },
      );
      if (!res.ok) throw new Error((await res.json())?.detail || "Cancel failed");
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Session cancelled",
        description: cancelIsLate
          ? "Weekly credit forfeited per the late-cancel policy."
          : "Your credit has been refunded.",
      });
      setCancelTarget(null);
      qc.invalidateQueries({ queryKey: ["my-sessions", user?.id] });
      qc.invalidateQueries({ queryKey: ["session-quota", user?.id] });
      qc.invalidateQueries({ queryKey: ["available-slots"] });
    },
    onError: (err) => {
      toast({
        title: "Cancel failed",
        description: err?.message || "Try again in a moment.",
        variant: "destructive",
      });
    },
  });

  // Reschedule = cancel current (refunded — button disabled inside 24h so
  // the refund is guaranteed) + scroll to the date picker for re-book.
  const scrollRef = useRef(null);
  const rescheduleMut = useMutation({
    mutationFn: async (sessId) => cancelMut.mutateAsync({ sessId, forceLate: false }),
    onSuccess: () => {
      toast({
        title: "Pick a new time",
        description: "Your previous session was cancelled and your credit refunded.",
      });
      scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  });

  // --- Render --------------------------------------------------------------
  const limit = quota?.limit ?? 1;
  const used = quota?.used ?? 0;
  const remaining = Math.max(0, limit - used);
  const progress = limit > 0 ? Math.min(100, ((limit - remaining) / limit) * 100) : 0;
  const remainingLabel = quota?.mode === "weekly" ? "this week" : "this month";
  const resetCountdown = quota?.seconds_until_reset != null
    ? fmtCountdown(quota.seconds_until_reset)
    : "—";
  const cantBook = remaining === 0 || !selectedSlot;

  return (
    <div
      data-testid="session-scheduler-page"
      className="-mx-3 sm:-mx-6 -my-4 px-4 sm:px-6 py-5 min-h-[100dvh] text-white relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #020617 0%, #030B1D 30%, #071326 75%, #000000 100%)",
      }}
    >
      {/* Decorative blue radial bloom */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 right-0 w-[480px] h-[480px] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, rgba(0,180,255,0.35), transparent 70%)" }}
      />

      {/* Header — back arrow lives in the TopBar (see TopBar.jsx, the
          left-corner slot is repurposed from "Scan Meal" to a back button
          on this route), so we just render the title block here. */}
      <div className="relative mb-5">
        <h1
          data-testid="scheduler-title"
          className="font-display font-extrabold tracking-tight text-xl sm:text-3xl lg:text-4xl whitespace-nowrap"
        >
          Session Scheduler
        </h1>
        <p className="text-xs sm:text-sm text-[#A8B3CF] mt-1 max-w-[300px]">
          Premium members: 1 video session per week with your trainer.
        </p>
      </div>

      {/* Weekly Session Card */}
      <Card glow>
        <div className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5">
          <div
            className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-full flex items-center justify-center border"
            style={{
              borderColor: "rgba(0,180,255,0.35)",
              background: "rgba(0,180,255,0.08)",
              boxShadow: "0 0 32px -10px rgba(0,180,255,0.55)",
            }}
          >
            <Video className="w-7 h-7 text-[#00B4FF]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-extrabold text-lg sm:text-2xl tracking-tight leading-tight">
              Your Weekly Session
            </h2>
            <p className="text-xs sm:text-sm text-white/80 mt-0.5">
              <span className="text-[#00B4FF] font-bold">{remaining} of {limit}</span>{" "}
              video session{limit === 1 ? "" : "s"} available {remainingLabel}.
            </p>
            <div className="mt-3 h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progress}%`,
                  background: "linear-gradient(90deg, #00C6FF 0%, #00A6FF 50%, #007BFF 100%)",
                  boxShadow: "0 0 14px rgba(0,180,255,0.5)",
                }}
              />
            </div>
            <p className="text-xs text-[#A8B3CF] mt-2">
              Resets in <span className="text-[#00B4FF] font-bold">{resetCountdown}</span>
            </p>
          </div>
          <div className="hidden sm:flex flex-col items-center gap-1 text-[#00B4FF]">
            <Video className="w-8 h-8" />
            <span className="text-sm font-bold">Video Call</span>
            <span className="text-[10px] text-white/55">60 mins</span>
          </div>
        </div>
      </Card>

      {/* Book Your Session Card */}
      <Card className="mt-4" ref={scrollRef}>
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="font-display font-extrabold text-lg sm:text-2xl tracking-tight">
                Book Your Session
              </h2>
              <p className="text-xs sm:text-sm text-[#A8B3CF] mt-0.5">
                Select a date and time that works for you.
              </p>
            </div>
            <span
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white/85"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Clock className="w-3.5 h-3.5 text-[#00B4FF]" /> CST <ChevronDown className="w-3.5 h-3.5 opacity-60" />
            </span>
          </div>

          {/* Date carousel — grid of evenly-sized cards (7 per row) so they
              always fit on the smallest mobile widths. Defaults to 7 days
              (one row); tapping "Show more" reveals an extra week below
              for users planning around a vacation. */}
          <div className="mt-4 grid grid-cols-7 gap-1 sm:gap-2">
            {visibleDates.map((d, i) => {
              const isSelected = localDateKey(d) === dateKey;
              const isToday = i === 0;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedDate(d)}
                  data-testid={`date-card-${localDateKey(d)}`}
                  className="flex flex-col items-center justify-center w-full h-[80px] sm:h-[88px] rounded-2xl border transition px-0.5"
                  style={
                    isSelected
                      ? {
                          background: "linear-gradient(180deg, rgba(0,180,255,0.20) 0%, rgba(0,114,255,0.08) 100%)",
                          borderColor: "#00B4FF",
                          boxShadow: "0 0 22px rgba(0,180,255,0.30)",
                        }
                      : {
                          background: "rgba(255,255,255,0.03)",
                          borderColor: "rgba(255,255,255,0.05)",
                        }
                  }
                >
                  <span className={`text-[9px] sm:text-[11px] uppercase tracking-wider font-bold ${isSelected ? "text-[#00B4FF]" : "text-[#A8B3CF]"}`}>
                    {isToday ? "Today" : WEEKDAY_SHORT[d.getDay()]}
                  </span>
                  <span className={`text-[8px] sm:text-[10px] uppercase tracking-wider mt-0.5 ${isSelected ? "text-[#00B4FF]/80" : "text-white/45"}`}>
                    {MONTH_SHORT[d.getMonth()]}
                  </span>
                  <span className={`text-base sm:text-lg font-extrabold tracking-tight mt-0.5 ${isSelected ? "text-white" : "text-white/80"}`}>
                    {d.getDate()}
                  </span>
                  {isSelected && <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-[#00B4FF]" />}
                </button>
              );
            })}
          </div>
          {/* Pagination toggle — only renders when there are more days to
              reveal (or hide) so the row stays clean for casual bookers. */}
          {(daysShown < TOTAL_DAYS || daysShown > PAGE_SIZE) && (
            <div className="mt-2 flex justify-center">
              <button
                type="button"
                onClick={() =>
                  setDaysShown((d) => (d < TOTAL_DAYS ? TOTAL_DAYS : PAGE_SIZE))
                }
                data-testid="date-show-more-btn"
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase text-[#00B4FF] hover:bg-[#00B4FF]/10 transition"
              >
                {daysShown < TOTAL_DAYS ? "Show more" : "Show less"}
                <span aria-hidden="true">{daysShown < TOTAL_DAYS ? "→" : "←"}</span>
              </button>
            </div>
          )}

          {/* Available Times */}
          <div className="mt-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-[#00B4FF]" />
              <h3 className="text-sm sm:text-base font-bold">Available Times</h3>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {SLOT_TEMPLATE.map((s) => {
                const isBooked = bookedSet.has(s.time);
                const isPast = pastSet.has(s.time);
                const isDisabled = isBooked || isPast;
                const isSelected = selectedSlot === s.time && !isDisabled;
                const baseStyle = {
                  height: 64,
                  borderRadius: 16,
                  borderWidth: 1,
                  borderStyle: "solid",
                };
                let style;
                if (isBooked) {
                  style = {
                    ...baseStyle,
                    background: "rgba(255,77,109,0.05)",
                    borderColor: "rgba(255,77,109,0.30)",
                    cursor: "not-allowed",
                  };
                } else if (isPast) {
                  style = {
                    ...baseStyle,
                    background: "rgba(255,255,255,0.02)",
                    borderColor: "rgba(255,255,255,0.04)",
                    cursor: "not-allowed",
                  };
                } else if (isSelected) {
                  style = {
                    ...baseStyle,
                    background: "linear-gradient(180deg, rgba(0,180,255,0.20) 0%, rgba(0,123,255,0.10) 100%)",
                    borderColor: "#00B4FF",
                    boxShadow: "0 0 22px rgba(0,180,255,0.30)",
                  };
                } else {
                  style = {
                    ...baseStyle,
                    background: "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))",
                    borderColor: "rgba(255,255,255,0.05)",
                  };
                }
                const tidSuffix = isBooked ? "-booked" : isPast ? "-past" : "";
                return (
                  <button
                    key={s.time}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => setSelectedSlot(s.time)}
                    data-testid={`time-slot-${s.time}${tidSuffix}`}
                    className="flex flex-col items-center justify-center transition active:scale-[0.99]"
                    style={style}
                  >
                    <span
                      className={`text-base font-extrabold ${
                        isBooked
                          ? "text-[#FF4D6D]/70 line-through"
                          : isPast
                          ? "text-white/30 line-through"
                          : isSelected
                          ? "text-[#00B4FF]"
                          : "text-white"
                      }`}
                    >
                      {s.label}
                    </span>
                    <span
                      className={`text-[10px] tracking-wider uppercase mt-0.5 ${
                        isBooked
                          ? "text-[#FF4D6D]/60"
                          : isPast
                          ? "text-white/30"
                          : "text-[#A8B3CF]"
                      }`}
                    >
                      {isBooked ? "Booked" : isPast ? "Past" : "CST"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Book CTA */}
          <button
            type="button"
            disabled={cantBook || bookMut.isPending}
            onClick={() => bookMut.mutate()}
            data-testid="book-session-btn"
            className="mt-5 w-full rounded-2xl font-extrabold text-white text-base tracking-wide flex items-center justify-center gap-2 active:scale-[0.99] transition disabled:opacity-50"
            style={{
              height: 60,
              background: "linear-gradient(90deg, #00C6FF 0%, #00A6FF 50%, #007BFF 100%)",
              boxShadow: cantBook ? "none" : "0 10px 35px rgba(0,180,255,0.35)",
            }}
          >
            <CalendarPlus className="w-5 h-5" /> {bookMut.isPending ? "Booking…" : "Book Session"}
          </button>

          <p className="mt-3 text-center text-xs text-[#7E8AA7]">
            <ShieldCheck className="inline w-3.5 h-3.5 mr-1 align-text-bottom" />
            You can reschedule or cancel up to{" "}
            <span className="text-[#00B4FF] font-bold">24 hours</span> before your session.
          </p>
        </div>
      </Card>

      {/* Upcoming Session Card */}
      {nextUpcoming && (
        <Card className="mt-4">
          <UpcomingRow
            session={nextUpcoming}
            onJoin={() => navigate(`/video-call/${nextUpcoming.id}`)}
            onCancel={() => setCancelTarget(nextUpcoming)}
            onReschedule={() => rescheduleMut.mutate(nextUpcoming.id)}
            rescheduling={rescheduleMut.isPending}
          />
        </Card>
      )}

      {/* Cancel-confirmation AlertDialog. Copy and the confirm-button colour
          change based on whether we're inside the 24h refund window. */}
      <AlertDialog
        open={!!cancelTarget}
        onOpenChange={(open) => { if (!open) setCancelTarget(null); }}
      >
        <AlertDialogContent
          data-testid="cancel-confirm-dialog"
          className="bg-[#0B1220] border-sky-500/20 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              {cancelIsLate ? "Cancel within 24 hours?" : "Cancel this session?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              {cancelIsLate ? (
                <>
                  This session starts in about{" "}
                  <span className="text-[#FF4D6D] font-bold">
                    {cancelHoursUntil != null ? Math.max(0, cancelHoursUntil).toFixed(1) : "0"} hours
                  </span>
                  , inside our {CANCEL_REFUND_HOURS}-hour cancellation window. Cancelling now
                  will <span className="text-[#FF4D6D] font-bold">forfeit your weekly session credit</span>{" "}
                  — it won&apos;t roll over to next week.
                </>
              ) : (
                <>
                  You&apos;re cancelling more than {CANCEL_REFUND_HOURS} hours before start, so your
                  weekly credit will be refunded and you can re-book at any time.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-confirm-keep-btn"
              className="bg-transparent border-white/15 text-white hover:bg-white/5"
            >
              Keep Session
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cancel-confirm-confirm-btn"
              onClick={() =>
                cancelMut.mutate({ sessId: cancelTarget?.id, forceLate: cancelIsLate })
              }
              className={
                cancelIsLate
                  ? "bg-[#FF4D6D] hover:bg-[#ff6680] text-white"
                  : "bg-[#00B4FF] hover:bg-[#33c4ff] text-[#020617]"
              }
            >
              {cancelIsLate ? "Cancel & Forfeit Credit" : "Cancel & Refund"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bottom spacing so the AppLayout bottom nav doesn't cover content */}
      <div className="h-24" />
    </div>
  );
}

// --- Subcomponents ---------------------------------------------------------

const Card = React.forwardRef(function Card({ children, className = "", glow = false }, ref) {
  return (
    <div
      ref={ref}
      className={`relative rounded-3xl border backdrop-blur-sm ${className}`}
      style={{
        background: "rgba(8,14,30,0.95)",
        borderColor: "rgba(50,90,160,0.25)",
        boxShadow: glow
          ? "0 8px 40px -16px rgba(0,180,255,0.25), inset 0 1px 0 rgba(255,255,255,0.04)"
          : "inset 0 1px 0 rgba(255,255,255,0.03)",
      }}
    >
      {children}
    </div>
  );
});

function UpcomingRow({ session, onJoin, onCancel, onReschedule, rescheduling }) {
  const start = new Date(session.date);
  const startTs = start.getTime();

  // Re-tick every second whenever we're within an hour of start so the
  // countdown stays live. Outside that window we just tick once a minute
  // because nothing is changing fast.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const ms = startTs - Date.now();
    const fast = ms > -60 * 60_000 && ms < 60 * 60_000;
    const id = setInterval(() => setNow(Date.now()), fast ? 1000 : 60_000);
    return () => clearInterval(id);
  }, [startTs, now]);

  const JOIN_WINDOW_MIN = 15;
  const SESSION_DURATION_MIN = 60;
  const msUntilStart = startTs - now;
  const msUntilJoinable = msUntilStart - JOIN_WINDOW_MIN * 60_000;
  const sessionEndsAt = startTs + SESSION_DURATION_MIN * 60_000;
  const canJoin = msUntilJoinable <= 0 && now <= sessionEndsAt;
  const showCountdown = !canJoin && msUntilStart > 0 && msUntilStart <= 60 * 60_000;
  const hoursUntil = msUntilStart / 36e5;
  const withinLateWindow = hoursUntil < CANCEL_REFUND_HOURS;

  const dateLabel = start.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
  const timeLabel = start.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", timeZone: "America/Chicago", timeZoneName: "short",
  });
  // Countdown copy: pre-join we count down to the moment the button
  // unlocks (T-15m). Inside the join window we show time remaining in
  // the session itself so the user knows how long they can still hop on.
  const cdMs = canJoin ? Math.max(0, sessionEndsAt - now) : Math.max(0, msUntilJoinable);
  const cdMin = Math.floor(cdMs / 60_000);
  const cdSec = Math.floor((cdMs % 60_000) / 1000);
  const countdownText = `${String(cdMin).padStart(2, "0")}:${String(cdSec).padStart(2, "0")}`;

  return (
    <div className="p-4 sm:p-5 flex items-start gap-3">
      <div
        className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-2xl flex items-center justify-center border"
        style={{ borderColor: "rgba(0,180,255,0.35)", background: "rgba(0,180,255,0.08)" }}
      >
        <CalendarCheck className="w-7 h-7 text-[#00B4FF]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-extrabold text-lg sm:text-xl tracking-tight">
          Upcoming Session
        </h3>
        <div className="mt-1.5 space-y-1 text-sm">
          <p className="flex items-center gap-2 text-white/85">
            <CalendarIcon className="w-3.5 h-3.5 text-[#A8B3CF]" /> {dateLabel}
          </p>
          <p className="flex items-center gap-2 text-white/85">
            <Clock className="w-3.5 h-3.5 text-[#A8B3CF]" /> {timeLabel}
          </p>
          <p className="flex items-center gap-2 text-white/65 text-xs">
            <Video className="w-3.5 h-3.5 text-[#A8B3CF]" /> Video Call • 60 mins
          </p>
          {withinLateWindow && !canJoin && (
            <p
              data-testid="upcoming-late-warning"
              className="flex items-center gap-2 text-[#FF4D6D]/85 text-[11px] font-bold tracking-wide uppercase mt-1"
            >
              Inside {CANCEL_REFUND_HOURS}h window — cancel forfeits credit
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        {canJoin ? (
          <button
            type="button"
            onClick={onJoin}
            data-testid="join-video-btn"
            className="px-3 py-2 rounded-2xl text-white text-xs font-extrabold flex flex-col items-center gap-0.5"
            style={{
              background: "linear-gradient(90deg, #00C6FF 0%, #00A6FF 50%, #007BFF 100%)",
              boxShadow: "0 6px 22px -8px rgba(0,180,255,0.7)",
            }}
          >
            <span className="flex items-center gap-1.5">
              <Video className="w-4 h-4" /> Join
            </span>
            <span
              data-testid="join-countdown-remaining"
              className="text-[9px] font-mono tabular-nums opacity-80"
            >
              {countdownText} left
            </span>
          </button>
        ) : showCountdown ? (
          <div
            data-testid="join-countdown-pending"
            className="px-3 py-2 rounded-2xl text-xs font-extrabold border text-center"
            style={{ borderColor: "#00B4FF", background: "rgba(0,180,255,0.06)", color: "#00B4FF" }}
          >
            <div className="text-[9px] tracking-wider uppercase opacity-80">Join in</div>
            <div className="font-mono tabular-nums text-base">{countdownText}</div>
          </div>
        ) : (
          <button
            type="button"
            disabled={withinLateWindow || rescheduling}
            onClick={onReschedule}
            data-testid="reschedule-btn"
            title={
              withinLateWindow
                ? `Reschedule locked inside ${CANCEL_REFUND_HOURS}h of start`
                : "Cancel current and pick a new time"
            }
            className="px-3 py-2 rounded-2xl text-[#00B4FF] text-xs font-extrabold border disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderColor: "#00B4FF", background: "transparent" }}
          >
            <CalendarClock className="inline w-4 h-4 mr-1" />
            {rescheduling ? "…" : "Reschedule"}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          data-testid="cancel-session-btn"
          className="px-3 py-2 rounded-2xl text-[#FF4D6D] text-xs font-extrabold border"
          style={{ borderColor: "rgba(255,60,90,0.6)", background: "transparent" }}
        >
          <Trash2 className="inline w-4 h-4 mr-1" /> Cancel
        </button>
      </div>
    </div>
  );
}
